var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SpriteManager } from '@babylonjs/core/Sprites/spriteManager';
import { Sprite } from '@babylonjs/core/Sprites/sprite';
import { spriteOptionsFromQuery, describeSpriteOptions } from '../../Utils/spriteOptions';
function isAtlasDescriptor(json) {
    if (!json || typeof json !== 'object')
        return false;
    const d = json;
    return (typeof d['frameSize'] === 'number' &&
        typeof d['cols'] === 'number' &&
        typeof d['rows'] === 'number' &&
        typeof d['layout'] === 'object' &&
        d['layout'] !== null);
}
// Ordered to match rot=0→south, increasing clockwise
const DIRECTIONS = [
    'south',
    'south-west',
    'west',
    'north-west',
    'north',
    'north-east',
    'east',
    'south-east',
];
const TAU = 2 * Math.PI;
const FRAME_MS = 150;
export const SPRITE_WORLD_SIZE = 3.0;
const ATLAS_URL = '/sprites/bunny/atlas.png';
const ATLAS_JSON_URL = '/sprites/bunny/atlas.json';
/**
 * How long the atlas PNG may take to reach the GPU before we treat it as broken.
 * A sprite whose texture never becomes ready is silently invisible: the nameplate
 * and shadow still render, so the failure is otherwise indistinguishable from a
 * correctly spawned bunny.
 */
export const TEXTURE_READY_TIMEOUT_MS = 8000;
function asAtlasTexture(value) {
    if (!value || typeof value !== 'object')
        return null;
    const candidate = value;
    const hasApi = typeof candidate.isReady === 'function' && typeof candidate.getBaseSize === 'function';
    return hasApi ? value : null;
}
/** Canvas conversion needs a DOM; unit tests and any headless host will not have one. */
function canUseCanvasAtlas() {
    return typeof document !== 'undefined' && typeof Image !== 'undefined';
}
/** Redraws the atlas through a 2D canvas, producing the same kind of source the grass uses. */
function toCanvasDataUrl(url) {
    return __awaiter(this, void 0, void 0, function* () {
        const image = yield new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`atlas image failed to load: ${url}`));
            img.src = url;
        });
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context)
            throw new Error('2D context unavailable for atlas conversion');
        context.drawImage(image, 0, 0);
        return canvas.toDataURL('image/png');
    });
}
function asEngineFacts(scene) {
    if (!scene || typeof scene !== 'object')
        return null;
    const getEngine = scene.getEngine;
    if (typeof getEngine !== 'function')
        return null;
    const engine = getEngine.call(scene);
    return engine && typeof engine === 'object' ? engine : null;
}
/** Maps entity Y-rotation (radians, Babylon.js convention) to nearest 8-direction string. */
export function directionFromAngle(rot) {
    const normalized = ((rot % TAU) + TAU) % TAU;
    const index = (Math.round((normalized / TAU) * 8) + 4) % 8;
    return DIRECTIONS[index];
}
export class BunnySpriteRenderer {
    constructor(_scene) {
        this._scene = _scene;
        this._manager = null;
        this._sprite = null;
        this._atlasData = null;
        this._currentState = null;
        this._currentDir = null;
        this._textureWatchdog = null;
    }
    load() {
        return __awaiter(this, void 0, void 0, function* () {
            let response;
            try {
                response = yield fetch(ATLAS_JSON_URL);
            }
            catch (err) {
                throw new Error(`BunnySpriteRenderer: failed to fetch atlas descriptor: ${err}`);
            }
            if (!response.ok) {
                throw new Error(`BunnySpriteRenderer: atlas descriptor not found (${response.status})`);
            }
            let json;
            try {
                json = yield response.json();
            }
            catch (err) {
                throw new Error(`BunnySpriteRenderer: atlas descriptor is not valid JSON: ${err}`);
            }
            if (!isAtlasDescriptor(json)) {
                throw new Error('BunnySpriteRenderer: atlas.json has unexpected shape');
            }
            this._atlasData = json;
            const spriteOptions = spriteOptionsFromQuery(typeof window !== 'undefined' ? window.location.search : '');
            console.info('[BUNNY] sprite options:', describeSpriteOptions(spriteOptions));
            // The grass renders on devices where the bunny does not, and it feeds its
            // manager a canvas data URL rather than a network URL. This reproduces that.
            let atlasSource = ATLAS_URL;
            if (spriteOptions.useCanvasAtlas && canUseCanvasAtlas()) {
                try {
                    atlasSource = yield toCanvasDataUrl(ATLAS_URL);
                }
                catch (err) {
                    console.error('BunnySpriteRenderer: canvas atlas conversion failed, using the URL:', err);
                }
            }
            this._manager = new SpriteManager('bunnyManager', atlasSource, spriteOptions.managerCapacity, { width: this._atlasData.frameSize, height: this._atlasData.frameSize }, this._scene);
            this._manager.isPickable = true;
            this._sprite = new Sprite('bunny', this._manager);
            this._sprite.width = SPRITE_WORLD_SIZE;
            this._sprite.height = SPRITE_WORLD_SIZE;
            this._sprite.isPickable = true;
            this._watchAtlasTexture();
        });
    }
    /**
     * Reports why a bunny might be invisible. Loading the descriptor and creating the
     * Sprite can both succeed while the atlas PNG never reaches the GPU, so readiness
     * of the texture — not existence of the sprite — is what decides visibility.
     */
    describeState() {
        var _a, _b, _c, _d, _e;
        const texture = asAtlasTexture((_a = this._manager) === null || _a === void 0 ? void 0 : _a.texture);
        const engine = asEngineFacts(this._scene);
        const baseSize = texture === null || texture === void 0 ? void 0 : texture.getBaseSize();
        const caps = (_b = engine === null || engine === void 0 ? void 0 : engine.getCaps) === null || _b === void 0 ? void 0 : _b.call(engine);
        return {
            atlasUrl: ATLAS_URL,
            descriptorLoaded: this._atlasData !== null,
            textureReady: (_c = texture === null || texture === void 0 ? void 0 : texture.isReady()) !== null && _c !== void 0 ? _c : false,
            textureBaseSize: baseSize ? `${baseSize.width}x${baseSize.height}` : 'unknown',
            webGLVersion: (_d = engine === null || engine === void 0 ? void 0 : engine.webGLVersion) !== null && _d !== void 0 ? _d : 'unknown',
            maxTextureSize: (_e = caps === null || caps === void 0 ? void 0 : caps.maxTextureSize) !== null && _e !== void 0 ? _e : 'unknown',
            animationState: this._currentState,
            direction: this._currentDir,
        };
    }
    /** Surfaces an atlas upload that never completes, instead of failing silently. */
    _watchAtlasTexture() {
        var _a, _b;
        const texture = asAtlasTexture((_a = this._manager) === null || _a === void 0 ? void 0 : _a.texture);
        if (!texture || texture.isReady())
            return;
        this._textureWatchdog = setTimeout(() => {
            this._textureWatchdog = null;
            console.error(`BunnySpriteRenderer: atlas texture still not ready after ${TEXTURE_READY_TIMEOUT_MS}ms — ` +
                'the bunny will be invisible even though its nameplate and shadow render.', this.describeState());
        }, TEXTURE_READY_TIMEOUT_MS);
        (_b = texture.onLoadObservable) === null || _b === void 0 ? void 0 : _b.addOnce(() => this._clearTextureWatchdog());
    }
    _clearTextureWatchdog() {
        if (this._textureWatchdog === null)
            return;
        clearTimeout(this._textureWatchdog);
        this._textureWatchdog = null;
    }
    setMetadata(metadata) {
        if (this._sprite)
            this._sprite.metadata = metadata;
    }
    setPosition(pos) {
        if (!this._sprite)
            return;
        // Lift by half the sprite height so the bottom edge sits at ground level
        this._sprite.position.set(pos.x, pos.y + SPRITE_WORLD_SIZE * 0.25, pos.z);
    }
    playAnimation(state, rot) {
        var _a;
        if (!this._sprite || !this._atlasData)
            return;
        const dir = directionFromAngle(rot);
        if (state === this._currentState && dir === this._currentDir)
            return;
        const cell = (_a = this._atlasData.layout[state]) === null || _a === void 0 ? void 0 : _a[dir];
        if (!cell)
            return;
        this._currentState = state;
        this._currentDir = dir;
        this._sprite.playAnimation(cell.startCell, cell.startCell + cell.frameCount - 1, true, FRAME_MS);
    }
    dispose() {
        var _a, _b;
        this._clearTextureWatchdog();
        (_a = this._sprite) === null || _a === void 0 ? void 0 : _a.dispose();
        (_b = this._manager) === null || _b === void 0 ? void 0 : _b.dispose();
    }
}
